import React, { useMemo, useRef, useEffect } from "react";
import { useFrame, extend, ReactThreeFiber } from "@react-three/fiber";
import { Html, shaderMaterial } from "@react-three/drei";
import * as THREE from "three";
import { SPHERE_RADIUS } from "../LevelMenuIslands";
import { ConnectionStatus } from "../../types/Status.types";

interface ConnectionLineTubeProps {
  start: THREE.Vector3;
  end: THREE.Vector3;
  status: ConnectionStatus;
  npcName: string;
  sourceMapId: string;
  targetMapId: string;
}

// Define ShaderMaterial using drei helper
const ConnectionLineMaterial = shaderMaterial(
  {
    time: 0,
    baseOpacity: 0.7,
    segmentOpacity: 1.0,
    speed: 0.5,
    isLocked: 0,
    color: new THREE.Color(1, 1, 1)
  },
  // Vertex Shader
  `
    varying vec2 vUv;
    #include <common>
    #include <logdepthbuf_pars_vertex>
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      #include <logdepthbuf_vertex>
    }
  `,
  // Fragment Shader
  `
    varying vec2 vUv;
    uniform float time;
    uniform float baseOpacity;
    uniform float segmentOpacity;
    uniform float speed;
    uniform float isLocked; // 0.0 for false, 1.0 for true
    uniform vec3 color;
    #include <common>
    #include <logdepthbuf_pars_fragment>

    void main() {
      #include <logdepthbuf_fragment>
      float alpha = baseOpacity;

      // Always animate, regardless of lock status
      // 4 segments along the line (interval 1/4)
      // vUv.x goes from 0 to 1 along the tube
      float frequency = 4.0;

      // Moving pattern
      float t = vUv.x * frequency - time * speed;

      // Create a pulse
      float cycle = fract(t);

      // Use smoothstep for better edge quality
      // Pulse active for 0.5 of the cycle (1/8 of total line)
      float pulse = step(0.5, cycle);

      alpha = mix(baseOpacity, segmentOpacity, pulse);

      gl_FragColor = vec4(color, alpha);
    }
  `
);

extend({ ConnectionLineMaterial });

// Add type definition for the new material
declare global {
  namespace JSX {
    interface IntrinsicElements {
      connectionLineMaterial: ReactThreeFiber.ThreeElement<typeof THREE.ShaderMaterial> & {
        isLocked?: number;
        baseOpacity?: number;
        segmentOpacity?: number;
        speed?: number;
        color?: THREE.ColorRepresentation;
      };
    }
  }
}

const ConnectionLineTube = ({ start, end, status, npcName, sourceMapId, targetMapId }: ConnectionLineTubeProps) => {
  const materialRef = useRef<any>(null);

  const isLocked = status === ConnectionStatus.LOCKED;
  
  // Curve calculation
  const curve = useMemo(() => {
    const distance = start.distanceTo(end);
    const isOutgoing = sourceMapId < targetMapId;
    const liftCoefficient = isOutgoing ? 0.4 : 0.15;
    const liftHeight = SPHERE_RADIUS + Math.max(3, distance * liftCoefficient);

    const midPoint = new THREE.Vector3()
      .addVectors(start, end)
      .divideScalar(2)
      .normalize()
      .multiplyScalar(liftHeight);

    return new THREE.QuadraticBezierCurve3(start, midPoint, end);
  }, [start, end, sourceMapId, targetMapId]);

  // Text position
  const textPosition = useMemo(() => {
    return curve.getPointAt(0.5).add(new THREE.Vector3(0, 0.5, 0));
  }, [curve]);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.time = state.clock.elapsedTime;
    }
  });

  return (
    <group>
      <mesh>
        <tubeGeometry args={[curve, 64, 0.02, 8, false]} />
        <connectionLineMaterial
          ref={materialRef}
          transparent
          depthWrite={false}
          side={THREE.DoubleSide}
          isLocked={isLocked ? 1.0 : 0.0}
          baseOpacity={isLocked ? 0.1 : 0.3}
          segmentOpacity={isLocked ? 0.2 : 1.0}
          speed={0.5}
          color={isLocked ? '#521616' : '#FBF2D3'}
        />
      </mesh>

      <Html position={textPosition} center>
        <span style={{
          color: '#FBF2D3',
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '13px',
          fontWeight: 400,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          opacity: isLocked ? 0.5 : 1,
        }}>
          {npcName}
        </span>
      </Html>
    </group>
  );
};

export default ConnectionLineTube;
